import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Scrollbar from "react-scrollbars-custom";

import onClickOutside from "react-onclickoutside";

import SearchInput from "../SearchInput/SearchInput";

import "./Select.scss";

class Select extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      search: "",
      focus: 0
    };
  }

  close = () => {
    this.setState({ open: false, search: "" });
    this.props.onClose && this.props.onClose();
  };

  onKeyDown = e => {
    let focus;

    if (e.keyCode == 13) {
      this.onSelect(this.props.options[this.state.focus]);
    } else if (e.keyCode == 38) {
      // up
      this.setState(prev => {
        if (this.state.focus == 0) {
          focus = this.props.options.length - 1;
        } else {
          focus = this.state.focus - 1;
        }

        this.options.children[focus].scrollIntoView(true);

        return { focus };
      });
    } else if (e.keyCode == 40) {
      // down
      this.setState(prev => {
        if (this.state.focus == this.props.options.length - 1) {
          focus = 0;
        } else {
          focus = this.state.focus + 1;
        }

        this.options.children[focus].scrollIntoView(false);

        return { focus };
      });
    }
  };

  onSelect = selected => {
    this.props.onSelect(selected);
    this.setState({ selected });
    this.close();
  };

  getDisplayValue = () => {
    return this.state.selected;
  };

  toggle = () => {
    if (this.props.disabled) {
      return;
    }

    this.setState(prevState => {
      return { open: true, focus: 0 };
    });
  };

  handleClickOutside = () => {
    this.close();
  };

  handleSearchChange = e => {
    if (this.props.handleSearchChange) {
      this.props.handleSearchChange(e);
      this.setState({ focus: 0 });
    } else {
      this.setState({ search: e.target.value, focus: 0 });
    }
  };

  renderOption = option => {
    return (
      <div className="select__option-content">
        {option.color && (
          <span
            className="select__option-color"
            style={{ backgroundColor: option.color }}
          />
        )}
        <span className="select__option-text">{option.label}</span>
        {option.id && <span className="select__option-id">[{option.id}]</span>}
      </div>
    );
  };

  renderSearch = () => {
    return <SearchInput onChange={this.handleSearchChange} />;
  };

  renderSearchHelp = () => {
    return (
      <span className="select__search-help">Введите 2 первых символа</span>
    );
  };

  getOptions = () => {
    return this.props.options;
  };

  getFilteredOptions = () => {
    if (this.state.search === "") {
      return this.getOptions();
    }

    return this.getOptions().filter(option => {
      return (
        option.label.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0
      );
    });
  };

  renderOptions = () => {
    const filteredOptions = this.getFilteredOptions();

    return (
      <div className="select__options" onKeyDown={this.onKeyDown}>
        {this.isSearchable() && this.renderSearch()}

        <div className="select__options-content">
          {this.mustShowSearchHelp() && this.renderSearchHelp()}
          {this.isLoading() && (
            <span className="select__loading-text">Загрузка...</span>
          )}

          {this.props.placeholder && (
            <div
              className={"select__option"}
              key={"clear"}
              onClick={() =>
                this.onSelect({ label: this.props.placeholder, value: "" })
              }
            >
              {this.renderOption({ label: this.props.placeholder, value: "" })}
            </div>
          )}

          <div ref={element => (this.options = element)}>
            {filteredOptions.map((option, i) => {
              const className = classnames("select__option", {
                selected: option.value === this.props.value,
                "select__option-is-group": option.is_group,
                focused: this.state.focus == i
              });

              return (
                <div
                  className={className}
                  key={option.value}
                  onClick={() => this.onSelect(option)}
                >
                  {this.renderOption(option)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  isSearchable = () => {
    return this.props.searchable || this.props.ajax;
  };

  isAjax = () => {
    return this.props.ajax;
  };

  isLoading = () => {
    return this.props.loading;
  };

  hasOptions = () => {
    return this.props.options.length !== 0;
  };

  mustShowSearchHelp = () => {
    return this.isAjax() && !this.isLoading() && !this.hasOptions();
  };

  static getSelectedValue = props => {
    let value = props.options.find(
      option =>
        option.value === props.value || option.value === props.initialValue
    );

    if (!value && value != 0) {
      value = {
        label: props.placeholder
      };
    }

    return value;
  };

  static getDerivedStateFromProps(props, state) {
    if (props.value === undefined) {
      if (!state.selected) {
        return {
          selected: Select.getSelectedValue(props)
        };
      }

      return state.selected;
    }

    if (state.selected === undefined || state.selected.value !== props.value) {
      return {
        selected: Select.getSelectedValue(props)
      };
    }
    return null;
  }

  render() {
    const className = classnames("select", this.props.className, {
      open: this.state.open,
      select__dark: this.props.theme === "dark",
      disabled: this.props.disabled
    });

    const selected = this.getDisplayValue();

    return (
      <div className={className}>
        {this.props.label && (
          <div className="select__label">{this.props.label}</div>
        )}
        <div className="select__control" onClick={this.toggle}>
          <span className="select__value">{this.renderOption(selected)}</span>
          <span className="select__arrow" />
        </div>
        {this.state.open && this.renderOptions()}
      </div>
    );
  }
}

Select.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  onSearchChange: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  ajax: PropTypes.bool,
  searchable: PropTypes.bool,
  handleSearchChange: PropTypes.func,
  className: PropTypes.string,
  theme: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onClose: PropTypes.func
};

Select.defaultProps = {
  options: [],
  loading: false,
  searchable: false,
  placeholder: "",
  ajax: false,
  theme: "",
  disabled: false
};

export default onClickOutside(Select);
